// ══════════════════════════════════════════════════════════
// BILLING SERVICE
//
// Orquestador central del sistema de billing.
// Unifica planes (Supabase), features (Supabase),
// suscripciones (Supabase backend PHP) y checkout (backend PHP).
//
// FRONTEND NO PUEDE:
// - Calcular precios
// - Modificar plan_id, subscription_status, payment_status
// - Confirmar pagos
// - Activar suscripciones
// ══════════════════════════════════════════════════════════

import { getSupabaseClient } from '@/services/supabase/client';
import type { StreamingMode, BillingPeriod } from '@/types/checkout';
import type {
  PlanDisplay,
  BillingPlan,
  PlanCompatibilityInfo,
  SubscriptionDisplay,
} from '@/types/billing';

// ── HELPERS ──

function supabase() {
  return getSupabaseClient();
}

function parsePlanFeatures(features: unknown): Record<string, string | number | boolean> {
  if (!features || typeof features !== 'object' || Array.isArray(features)) return {};
  const record = features as Record<string, unknown>;
  const safe: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(record)) {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      safe[key] = value;
    }
  }
  return safe;
}

function formatPrice(amount: number, currency: string): string {
  if (amount === 0) return 'Gratis';
  const symbol = currency === 'USD' ? '$' : currency;
  return `${symbol}${amount}/mes`;
}

function formatYearlyPrice(amount: number | null, currency: string): string | null {
  if (amount === null || amount === 0) return null;
  const symbol = currency === 'USD' ? '$' : currency;
  return `${symbol}${amount}/año`;
}

function resolveStreamingModes(features: Record<string, string | number | boolean>): StreamingMode[] {
  const modes = features.streaming_modes;
  if (Array.isArray(modes)) return modes as StreamingMode[];
  // Fallback: if managed_streaming feature is present, include RFW_MANAGED
  const hasManaged = features.managed_streaming === true;
  return hasManaged ? ['EXTERNAL_STREAM', 'RFW_MANAGED'] : ['EXTERNAL_STREAM'];
}

function resolveNumericLimit(features: Record<string, string | number | boolean>, key: string, fallback: number): number {
  const val = features[key];
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const n = parseFloat(val);
    if (!Number.isNaN(n)) return n;
  }
  return fallback;
}

function enrichPlan(plan: PlanDisplay, featureCodes: string[]): BillingPlan {
  const features = plan.features;
  const streamingModes = resolveStreamingModes(features);

  return {
    ...plan,
    feature_codes: featureCodes,
    formatted_price_monthly: formatPrice(plan.price_monthly, plan.currency),
    formatted_price_yearly: formatYearlyPrice(plan.price_yearly, plan.currency),
    streaming_modes: streamingModes,
    has_domain: featureCodes.includes('custom_domain'),
    has_lives: resolveNumericLimit(features, 'max_lives', 0) > 0 || resolveNumericLimit(features, 'max_lives', 0) === -1,
    has_app: featureCodes.includes('custom_app'),
    storage_gb: resolveNumericLimit(features, 'storage_gb', 1),
    max_announcers: resolveNumericLimit(features, 'max_announcers', 1),
    max_photos: resolveNumericLimit(features, 'max_photos', 0),
    max_videos: resolveNumericLimit(features, 'max_videos', 0),
    max_lives: resolveNumericLimit(features, 'max_lives', 0),
    has_managed_streaming: featureCodes.includes('managed_streaming'),
    has_autodj: featureCodes.includes('autodj'),
  };
}

// ═══════════════ PUBLIC PLANS ═══════════════

/**
 * Obtener todos los planes activos y públicos desde Supabase.
 * Enriquecidos con feature_codes desde plan_features.
 *
 * Precio, moneda, features y límites: TODO desde BD.
 * NADA hardcodeado.
 */
export async function getPublicBillingPlans(): Promise<BillingPlan[]> {
  // 1. Obtener planes activos y públicos
  const { data: plans, error: plansError } = await supabase()
    .from('plans')
    .select('id, name, slug, description, price_monthly, price_yearly, features, is_active, is_public, sort_order, code, currency, created_at, updated_at')
    .eq('is_active', true)
    .eq('is_public', true)
    .order('sort_order', { ascending: true });

  if (plansError) throw new Error(`Error al obtener planes: ${plansError.message}`);
  if (!plans || plans.length === 0) return [];

  // 2. Obtener todos los feature_codes
  const planIds = plans.map((p) => p.id as string);
  const { data: allFeatures, error: featuresError } = await supabase()
    .from('plan_features')
    .select('plan_id, feature_code')
    .in('plan_id', planIds);

  if (featuresError) throw new Error(`Error al obtener features: ${featuresError.message}`);

  // 3. Agrupar features por plan_id
  const featuresByPlan: Record<string, string[]> = {};
  for (const f of allFeatures || []) {
    const pid = f.plan_id as string;
    if (!featuresByPlan[pid]) featuresByPlan[pid] = [];
    featuresByPlan[pid].push(f.feature_code as string);
  }

  // 4. Construir BillingPlan[]
  return plans.map((row) => {
    const display: PlanDisplay = {
      id: row.id as string,
      code: row.code as string,
      name: row.name as string,
      slug: row.slug as string,
      description: (row.description as string) || '',
      price_monthly: Number(row.price_monthly ?? 0),
      price_yearly: row.price_yearly ? Number(row.price_yearly) : null,
      currency: (row.currency as string) || 'USD',
      features: parsePlanFeatures(row.features),
      is_active: (row.is_active as boolean) ?? false,
      is_public: (row.is_public as boolean) ?? false,
      sort_order: (row.sort_order as number) ?? 0,
      feature_codes: featuresByPlan[row.id as string] || [],
      created_at: row.created_at as string,
      updated_at: row.updated_at as string,
    };

    return enrichPlan(display, featuresByPlan[row.id as string] || []);
  });
}

/**
 * Obtener un plan individual enriquecido.
 */
export async function getBillingPlanById(planId: string): Promise<BillingPlan | null> {
  const plans = await getPublicBillingPlans();
  return plans.find((p) => p.id === planId) ?? null;
}

/**
 * Obtener un plan por slug.
 */
export async function getBillingPlanBySlug(slug: string): Promise<BillingPlan | null> {
  const plans = await getPublicBillingPlans();
  return plans.find((p) => p.slug === slug) ?? null;
}

// ═══════════════ COMPATIBILITY ═══════════════

/**
 * Filtrar y verificar compatibilidad de planes según modalidad de streaming.
 *
 * - EXTERNAL_STREAM: todos los planes activos son compatibles.
 * - RFW_MANAGED: solo planes con managed_streaming en plan_features.
 */
export async function getCompatibleBillingPlans(
  streamingMode: StreamingMode,
): Promise<PlanCompatibilityInfo[]> {
  const plans = await getPublicBillingPlans();

  return plans.map((plan) => {
    const compatible = streamingMode === 'EXTERNAL_STREAM' || plan.has_managed_streaming;
    return {
      plan,
      compatible,
      reason: compatible
        ? undefined
        : 'Este plan no incluye streaming administrado. Seleccioná un plan Pro, Business o Premium.',
    };
  });
}

// ═══════════════ SUBSCRIPTION ═══════════════

/**
 * Obtener la suscripción activa de la organización del usuario autenticado.
 * Usa Supabase (subscriptions + plans + plan_features).
 */
export async function getMyBillingSubscription(): Promise<SubscriptionDisplay | null> {
  // 1. Profile → organization_id
  const { data: profile, error: profileError } = await supabase()
    .from('profiles')
    .select('organization_id')
    .single();

  if (profileError || !profile?.organization_id) return null;

  const orgId = profile.organization_id as string;

  // 2. Suscripción activa
  const { data: subscription, error: subError } = await supabase()
    .from('subscriptions')
    .select('*')
    .eq('organization_id', orgId)
    .in('status', ['active', 'trial', 'past_due'])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (subError || !subscription) return null;

  // 3. Plan asociado
  const { data: plan, error: planError } = await supabase()
    .from('plans')
    .select('id, name, slug, features, code')
    .eq('id', subscription.plan_id as string)
    .maybeSingle();

  if (planError || !plan) return null;

  // 4. Feature codes
  const { data: features, error: featsError } = await supabase()
    .from('plan_features')
    .select('feature_code')
    .eq('plan_id', plan.id as string);

  const featureCodes = featsError ? [] : (features || []).map((f) => f.feature_code as string);
  const parsedFeatures = parsePlanFeatures(plan.features);

  return {
    plan_name: plan.name as string,
    plan_slug: plan.slug as string,
    status: subscription.status as string,
    billing_period: (subscription.billing_period as BillingPeriod) || null,
    starts_at: subscription.starts_at as string | null,
    ends_at: subscription.ends_at as string | null,
    auto_renew: (subscription.auto_renew as boolean) ?? false,
    feature_codes: featureCodes,
    storage_gb: resolveNumericLimit(parsedFeatures, 'storage_gb', 0),
    has_managed_streaming: featureCodes.includes('managed_streaming'),
  };
}

// ═══════════════ FEATURE CHECK ═══════════════

/**
 * Verificar si una feature está habilitada para la organización actual.
 */
export async function hasBillingFeature(featureCode: string): Promise<boolean> {
  const sub = await getMyBillingSubscription();
  if (!sub) return false;
  if (sub.status !== 'active' && sub.status !== 'trial') return false;
  return sub.feature_codes.includes(featureCode);
}

// ═══════════════ RE-EXPORT ═══════════════

export { formatPrice, formatYearlyPrice, resolveNumericLimit, enrichPlan };